import { classToPlain, Exclude, Expose, plainToClass } from "class-transformer";
import * as t from "io-ts";
import { validateJSON } from "../web/validation";

/** Validator for error log types. */
export const ErrorLogValidator = t.type({
  deviceId: t.string,
  description: t.string,
  createDttm: t.number,
  regionId: t.string,
  authentication: t.string,
});

/** Validator for an array of error log types. */
export const ErrorLogArrayValidator = t.array(ErrorLogValidator);

export type ErrorLog = t.TypeOf<typeof ErrorLogValidator>;

/**
 * An error log.
 */
@Exclude()
export class ErrorLogEntity {
  @Expose() deviceId!: string;
  @Expose() description!: string;
  @Expose() regionId!: string;
  @Expose() authentication!: string;
  @Expose() private createDttm?: number;

  /**
   * Constructor for ErrorLogEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  /**
   * Create an ErrorLogEntity.
   *
   * @param firstName The trailer's first name.
   * @param lastName The trailer's last name.
   * @param type The type of trailer.
   * @param companyId The primary key of the trailer's employer.
   * @param id (optional) The primary key of the trailer.
   */
  static create(
    description: string,
    regionId: string,
    deviceId: string,
    auth: string,
    createDate: Date = new Date()
  ): ErrorLogEntity {
    const entity = new ErrorLogEntity();
    entity.description = description;
    entity.regionId = regionId;
    entity.deviceId = deviceId;
    entity.authentication = auth;
    entity.createDttm = createDate.getTime();
    return entity;
  }

  /**
   * Create an ErrorLogEntity from an ErrorLog type value.
   *
   * @param trailer The trailer type value.
   */
  static from(errorLog: ErrorLog): ErrorLogEntity {
    const entity: ErrorLogEntity = plainToClass(ErrorLogEntity, errorLog, {
      excludeExtraneousValues: true,
    });
    return entity;
  }

  /**
   * Deserializes the ErrorLogEntity to a valid ErrorLog type.
   * If validation fails, the promise will be rejected.
   */
  toJSON(): Promise<ErrorLog> {
    const object = classToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(ErrorLogValidator, object);
  }

  get createDate(): Date | undefined {
    /* istanbul ignore next */
    if (this.createDttm) {
      return new Date(this.createDttm);
    } else {
      return undefined;
    }
  }

  set createDate(date: Date | undefined) {
    /* istanbul ignore next */
    if (date) {
      this.createDttm = date.getTime();
    } else {
      this.createDttm = undefined;
    }
  }
}
